import { useEffect, useState } from "react";
import { RunSheet } from "@/components/run/RunSheet";
import { useOfficialWrite, type RiderSheetProps } from "./shared";
import type { ConsoleCell } from "@/components/headjudge/model";
interface Cell {
  id: string;
  heat_rider_id: string;
  judge_number: number;
  attempt_number: number;
  value: string | number;
  criterion_id: string | null;
  pass_number?: number | null;
  criterion_name?: string | null;
  source?: string;
}
export function ScoreCorrectionSheet(props: RiderSheetProps & { initialCell?: ConsoleCell }) {
  const { competitionId, heat, rider, transport, onClose } = props;
  const base = `/api/competitions/${competitionId}/heats/${heat.id}`;
  const [cells, setCells] = useState<Cell[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(false);
  const { busy, error, write, execute, undo } = useOfficialWrite(props);
  useEffect(() => {
    let active = true;
    void transport
      .get<Cell[]>(`${base}/correction-state?heat_rider_id=${rider.heat_rider_id}`)
      .then((rows) => {
        if (active) {
          const visible = rows.filter(
            (row) =>
              row.heat_rider_id === rider.heat_rider_id &&
              (row.criterion_id !== null ||
                !rows.some(
                  (criterion) =>
                    criterion.criterion_id !== null &&
                    criterion.heat_rider_id === row.heat_rider_id &&
                    criterion.judge_number === row.judge_number &&
                    criterion.attempt_number === row.attempt_number &&
                    criterion.pass_number === row.pass_number,
                )),
          );
          setCells(visible);
          const matches = visible.filter(
            (row) =>
              props.initialCell &&
              row.attempt_number === props.initialCell.attempt_number &&
              row.judge_number === props.initialCell.judge_number &&
              row.criterion_id === (props.initialCell.criterion_id ?? null),
          );
          const initial = matches.length === 1 ? matches[0] : undefined;
          if (initial) {
            setSelected(initial.id);
            setValue(String(initial.value));
          }
        }
      })
      .catch((e) => setLoadError(e.message));
    return () => {
      active = false;
    };
  }, [base, rider.heat_rider_id, transport, props.initialCell]);
  const cell = cells.find((c) => c.id === selected);
  return (
    <RunSheet title="Score correction" close={onClose}>
      <p>{rider.name}</p>
      <p>Corrected scores are locked to judge re-entry.</p>
      {(error || loadError) && <p role="alert">{error || loadError}</p>}
      {undo}
      {saved && <p role="status">Correction saved. This score is locked to judge re-entry.</p>}
      <label>
        Score cell
        <select
          value={selected}
          onChange={(e) => {
            const c = cells.find((c) => c.id === e.target.value);
            setSelected(e.target.value);
            setValue(c ? String(c.value) : "");
            setSaved(false);
          }}
        >
          <option value="">Select a score</option>
          {cells.map((c) => (
            <option key={c.id} value={c.id}>
              Attempt {c.attempt_number} · Judge {c.judge_number}
              {c.criterion_id ? ` · ${c.criterion_name ?? "criterion"}` : ""}
              {c.pass_number ? ` · Pass ${c.pass_number}` : ""} · {c.value}
            </option>
          ))}
        </select>
      </label>
      {cells.length === 0 && !loadError && <p>No score cells recorded for this rider.</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (cell)
            void write(async () => {
              await execute("score", {
                heat_rider_id: rider.heat_rider_id,
                judge_number: cell.judge_number,
                attempt_number: cell.attempt_number,
                criterion_id: cell.criterion_id,
                pass_number: cell.pass_number,
                value,
                reason: reason.trim(),
              });
              setSaved(true);
            }, false);
        }}
      >
        <label>
          Score
          <input
            required
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!cell || busy}
          />
        </label>
        <label>
          Reason
          <textarea
            required
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <button className="run-primary" disabled={!cell || busy || !reason.trim() || !value}>
          Save correction
        </button>
      </form>
      <button
        disabled={!cell || busy || !reason.trim() || (!saved && cell.source !== "official")}
        onClick={() =>
          cell &&
          void write(() =>
            execute("score_unlock", {
              heat_rider_id: rider.heat_rider_id,
              judge_number: cell.judge_number,
              attempt_number: cell.attempt_number,
              criterion_id: cell.criterion_id,
              pass_number: cell.pass_number,
              reason: reason.trim(),
            }),
          )
        }
      >
        Unlock — let the judge re-enter this score
      </button>
    </RunSheet>
  );
}
